import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { AccountProviderUserListSortCol, TAccountProviderUserList, TAccountProviderUserListResponse, TAccountProviderUserListQuery, TAccountProviderUserListSortCol, adaptAccountProviderUserFromListResponse, adaptAccountProviderUserListQueryToRequest } from '../../types';
import { BACKEND_ACCOUNT_PROVIDER_USER } from '../../backend-paths';


export const accountProviderUserListGetApi = createAsyncThunk(
	BACKEND_ACCOUNT_PROVIDER_USER,
	(payload: TAccountProviderUserListQuery | undefined, thunkApi) => {
		const params = adaptAccountProviderUserListQueryToRequest(payload);

		return HttpService.get<TAccountProviderUserListResponse>(BACKEND_ACCOUNT_PROVIDER_USER, { params }).then(result => {
			return adaptAccountProviderUserFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TAccountProviderUserList;
	query: TAccountProviderUserListQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: AccountProviderUserListSortCol.CREATED_AT,
	},
	error: undefined,
};

export const accountProviderUserListSlice = createSlice({
	name: 'accountProviderUserListSlice',
	initialState,
	reducers: {
		setAccountProviderUserListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setAccountProviderUserListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setAccountProviderUserListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setAccountProviderUserListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setAccountProviderUserListQuerySortCol(state, action: PayloadAction<TAccountProviderUserListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearAccountProviderUserList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[accountProviderUserListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[accountProviderUserListGetApi.fulfilled.type]: (state, action: PayloadAction<TAccountProviderUserList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[accountProviderUserListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useAccountProviderUserListSelector = () => useAppSelector(state => state[accountProviderUserListSlice.name]);

export const { clearAccountProviderUserList, setAccountProviderUserListQueryLimit, setAccountProviderUserListQueryOffset, setAccountProviderUserListQuerySort, setAccountProviderUserListQuerySortCol, setAccountProviderUserListQuerySearch } = accountProviderUserListSlice.actions;
